/**
 * <park-stage> — one stage of a staged, single-page flow (anton/forrest#1387).
 * Isomorphic: pure renderParkStage() shared by ds/ParkStage.astro and the
 * custom element (see docs/design-system-rebuild-plan.md).
 *
 * The pattern: a vertical sequence of stages where exactly one is OPEN, the
 * finished ones are FOLDED into a compact summary of what was chosen, and the
 * rest are UPCOMING. Folding is what turns a long form into a short, obviously
 * progressing sequence (booking-flow-ux FR-10).
 *
 * IMPORTANT — like <park-booking-calendar>, this is a SHELL whose live state is
 * owned by the page script (varaa.astro): it flips `data-state`, `aria-expanded`
 * and the summary text directly on the emitted nodes. Therefore
 * observedAttributes=[] and the SSR wrapper stamps `data-ssr` — a re-render
 * would wipe both this stage's script-populated children (the day strip, the
 * hour grid) and the state the script has set.
 *
 * WHY THE HEADER IS A BUTTON, ALWAYS. A folded stage must be reopenable by
 * pointer AND keyboard (FR-10a), and a customer who mistyped an email must not
 * have to restart (US-4b). A `<div>` with a click handler satisfies neither, so
 * the summary IS the control rather than carrying one.
 *
 * No-JS fallback: every stage renders OPEN (`data-state="open"`), so the whole
 * form is present and submittable without the script — the staging is an
 * enhancement, never the only way through (FR-10d's "state readable from a
 * static rendering", taken one step further).
 *
 * Attributes (all pre-resolved strings from the page locale):
 *   num, title, summary, edit-label, back-label, body-id.
 *
 * @dsSpec booking-flow-ux#FR-10 — the staged flow's per-stage chrome.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-stage";

export interface ParkStageProps {
	/** Displayed step number — the stage indicator's "which one is this". */
	num?: string;
	title?: string;
	/** The chosen value, shown once folded. Empty until the stage completes. */
	summary?: string;
	/** Accessible name for the reopen affordance on a folded stage. */
	editLabel?: string;
	/** Ties the header button to its body for `aria-controls`. */
	bodyId?: string;
	/** Label for the "go back a stage" control. Supplied only by stages that
	 *  HAVE a previous stage to return to — its presence is what decides whether
	 *  the control exists at all, so the component never has to guess its own
	 *  position in the sequence. */
	backLabel?: string;
}

/** Pure render: props + slot HTML → one stage's chrome. No DOM access.
 *
 *  a11y: the header is an `<h2>`-wrapped `<button aria-expanded aria-controls>`
 *  — the standard disclosure shape, so AT announces both the stage name and
 *  whether it is open. The step number is `aria-hidden` because it is already
 *  implied by document order and would otherwise be read as a bare digit before
 *  every title. */
export function renderParkStage(props: ParkStageProps, slot = ""): string {
	const bodyId = props.bodyId || "stage-body";
	// OUTSIDE the toggle and OUTSIDE the heading, in a wrapper that owns the
	// positioning. Not inside `.stage-toggle`, because that is itself a
	// `<button>` and nesting one in another is invalid and unreachable by
	// keyboard and AT. Not inside the `<h2>` either: a heading's accessible name
	// is computed from its contents, so a back control there would rename the
	// heading to "2 Choose a time Back" for anyone navigating by heading.
	// `has-back` lets the toggle reserve room for it without `:has()`.
	const back = props.backLabel
		? html`<button type="button" class="stage-back">${esc(props.backLabel)}</button>`
		: "";
	return html`<div class="stage${props.backLabel ? " has-back" : ""}" data-state="open"><div class="stage-headrow"><h2 class="stage-head"><button type="button" class="stage-toggle" aria-expanded="true" aria-controls="${esc(bodyId)}"><span class="stage-num" aria-hidden="true">${esc(props.num)}</span><span class="stage-title">${esc(props.title)}</span><span class="stage-summary" data-empty="true"></span><span class="stage-edit">${esc(props.editLabel)}</span></button></h2>${back}</div><div class="stage-body" id="${esc(bodyId)}">${slot}</div></div>`;
}

class ParkStage extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}

	protected render(): string {
		return renderParkStage(
			{
				num: this.attr("num"),
				title: this.attr("title"),
				summary: this.attr("summary"),
				editLabel: this.attr("edit-label"),
				backLabel: this.attr("back-label"),
				bodyId: this.attr("body-id"),
			},
			this.slotHtml,
		);
	}
}

defineElement(TAG, ParkStage);

export { ParkStage };
